import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section } from '../shared';

// Social icons removed per request

// Audio Quote Player
const AudioQuotePlayer = ({ isPlaying, onToggle }) => {
  return (
    <div className="mt-4 bg-dark-base/50 rounded-xl p-4">
      <p className="text-neon-orange text-sm mb-3 font-semibold">Audio Quote</p>
      
      <div className="flex items-center gap-4">
        <motion.button
          onClick={onToggle}
          className="w-12 h-12 rounded-full bg-neon-orange flex items-center justify-center text-white"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isPlaying ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16"/>
              <rect x="14" y="4" width="4" height="16"/>
            </svg>
          ) : (
            <svg className="w-5 h-5 ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </motion.button>
        
        <div className="flex-1">
          {/* Waveform visualization */}
          <div className="flex items-center gap-[2px] h-8">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                className="w-1 bg-neon-orange/50 rounded-full"
                animate={{
                  height: isPlaying 
                    ? [4, Math.random() * 24 + 4, 4] 
                    : Math.random() * 16 + 4,
                }}
                transition={{
                  duration: 0.3,
                  repeat: isPlaying ? Infinity : 0,
                  delay: i * 0.02,
                }}
              />
            ))}
          </div>
        </div>
        
        <span className="text-gray-500 text-sm">0:15</span>
      </div>
    </div>
  );
};

// Bio Modal Component
const BioModal = ({ member, isOpen, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Content */}
          <motion.div
            className="relative bg-dark-deeper border border-neon-orange/20 rounded-2xl max-w-lg w-full overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-dark-base flex items-center justify-center text-gray-400 hover:text-white hover:bg-neon-orange/20 transition-colors z-10"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header with Photo */}
            <div className="relative h-32 bg-gradient-to-r from-neon-orange/20 to-dark-deeper">
              <div className="absolute -bottom-16 left-1/2 -translate-x-1/2">
                <motion.div
                  className="w-32 h-32 rounded-full border-4 border-neon-orange overflow-hidden"
                  animate={{
                    boxShadow: [
                      '0 0 20px rgba(253, 128, 46, 0.3)',
                      '0 0 40px rgba(253, 128, 46, 0.5)',
                      '0 0 20px rgba(253, 128, 46, 0.3)',
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
            </div>

            {/* Content */}
            <div className="pt-20 pb-6 px-6 text-center">
              <h3 className="text-2xl font-heading font-bold text-white mb-1">{member.name}</h3>
              <p className="text-neon-orange font-semibold mb-4">{member.role}</p>
              
              {/* Social Icons removed */}
              
              {/* Full Bio */}
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                {member.fullBio}
              </p>
              
              {/* Audio Quote */}
              <AudioQuotePlayer 
                isPlaying={isPlaying} 
                onToggle={() => setIsPlaying(!isPlaying)} 
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Team Member Card
const TeamMemberCard = ({ member, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <motion.div
        className="group text-center cursor-pointer"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={() => setShowModal(true)}
      >
        {/* Photo Container */}
        <div className="relative mb-6 mx-auto w-36 h-36 md:w-44 md:h-44">
          {/* Outer glow ring */}
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              boxShadow: isHovered 
                ? '0 0 30px rgba(253, 128, 46, 0.5)' 
                : '0 0 0px rgba(253, 128, 46, 0)'
            }}
          />
          
          {/* Border ring */}
          <motion.div
            className="absolute inset-0 rounded-full border-4 transition-colors duration-300"
            animate={{
              borderColor: isHovered ? '#FD802E' : 'rgba(253, 128, 46, 0.2)',
            }}
          />
          
          {/* Photo */}
          <div className="absolute inset-2 rounded-full overflow-hidden">
            <img 
              src={member.image} 
              alt={member.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          </div>
          
          {/* Hover overlay */}
          <motion.div
            className="absolute inset-2 rounded-full bg-neon-orange/20 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
          >
            <span className="text-white text-sm font-semibold">View Bio</span>
          </motion.div>
        </div>

        {/* Name & Role */}
        <h3 className="text-lg font-heading font-bold text-white group-hover:text-neon-orange transition-colors">
          {member.name}
        </h3>
        <p className="text-neon-orange text-sm mb-3">{member.role}</p>
        <p className="text-gray-400 text-sm max-w-[200px] mx-auto mb-4">{member.shortBio}</p>
        {/* Social icons removed */}
      </motion.div>

      {/* Bio Modal */}
      <BioModal 
        member={member} 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
      />
    </>
  );
};

const TeamSection = () => {
  const team = [
    {
      name: "Olivia Bennett",
      role: "Founder & CTO",
      shortBio: "Former AI researcher. Visionary leader.",
      fullBio: `Olivia brings over 15 years of experience in AI and audio technology. Before founding Veritone Tech, she led advanced research at MIT, pioneering neural audio processing techniques. Her work has been published in Nature and IEEE, and she holds multiple patents in AI audio technology.`,
      image: "/team%20(4)_result.webp",
      socials: [
        { type: "twitter", href: "#" },
        { type: "linkedin", href: "#" },
      ]
    },
    {
      name: "Lucas Sterling",
      role: "Audio AI Director",
      shortBio: "Ex-Spotify engineer. Scalability expert.",
      fullBio: `Lucas Sterling spent 8 years at Spotify building audio systems for millions of users. At VeritoneX, he leads the engineering team to create somewhat scalable, reliable audio tools for creators of all sizes.`,
      image: "/team%20(2)_result.webp",
      socials: [
        { type: "linkedin", href: "#" },
        { type: "github", href: "#" },
      ]
    },
    {
      name: "Lisa Tavi",
      role: "Head of Product",
      shortBio: "Former Adobe product lead. UX enthusiast.",
      fullBio: `Lisa Tavi led product development for Adobe Audition and Premiere Pro’s audio features for 6 years. At Veritone Tech, she drives the creation of intuitive, impactful tools, ensuring every feature meets the needs of modern creators.`,
      image: "/team%20(3)_result.webp",
      socials: [
        { type: "twitter", href: "#" },
        { type: "linkedin", href: "#" },
      ]
    },
    {
      name: "William Edward",
      role: "Lead ML Engineer",
      shortBio: "PhD Neural Networks. Audio synthesis pioneer.",
      fullBio: `William Edward developed breakthrough algorithms for audio synthesis during his PhD at Stanford. His generative audio research has been cited over 2,000 times. At VeritoneX, he leads the machine learning team powering advanced audio enhancement features.`,
      image: "/team_result.webp",
      socials: [
        { type: "linkedin", href: "#" },
        { type: "github", href: "#" },
      ]
    },
  ];

  return (
    <Section id="team" className="bg-dark-base relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-neon-orange/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.span 
              className="text-neon-orange font-semibold text-sm uppercase tracking-wider"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Our Team
            </motion.span>
          
            <motion.h2 
              className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              The Visionaries of VeritoneX
            </motion.h2>
          
            <motion.p 
              className="text-gray-400 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Explore each member’s story and hear their personal audio quote.
            </motion.p>
        </div>

        {/* Team Grid - Flex Wrap */}
        <div className="flex flex-wrap justify-center gap-8 md:gap-12">
          {team.map((member, i) => (
            <TeamMemberCard key={i} member={member} index={i} />
          ))}
        </div>
      </div>
    </Section>
  );
};

export default TeamSection;
